import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import pendingIcon from "../../Photos/Newuiphotos/VerificationCenter/pendingIcon.webp";
import approvedIcon from "../../Photos/Newuiphotos/VerificationCenter/approvedIcon.webp";
import deniedIcon from "../../Photos/Newuiphotos/VerificationCenter/deniedIcon.webp";
import approvedBtnTick from "../../Photos/Newuiphotos/VerificationCenter/approvedBtnTick.webp";
import rejectedBtnCross from "../../Photos/Newuiphotos/VerificationCenter/rejectedBtnCross.webp";
import exclamationIcon from "../../Photos/Newuiphotos/VerificationCenter/exclamationIcon.webp";
import { useApolloClient, useMutation } from "@apollo/client";
import { GET_CUSTOM_DONATIONS_FOR_REVIEW } from "../../graphql/DonationLinkReview/queries";
import { VERIFY_CUSTOM_DONATION } from "../../graphql/DonationLinkReview/mutation";
import toast from "react-hot-toast";
import moment from "moment";
import DonationLinkReviewModal, {
  DonationLinkReviewItem,
} from "./DonationLinkReviewModal";
import {
  isApprovedDonationStatus,
  isSafeHttpUrl,
  openSafePaymentLink,
} from "../../helpers/donationPaymentLink";
import PaymentLinkWarningDialog from "./PaymentLinkWarningDialog";
import {
  fetchAllPages,
  formatTabCount,
  matchesDonationSearch,
  REVIEW_PAGE_SIZE,
} from "./donationLinkReviewQueue";
import DonationEntityIdLink from "./DonationEntityIdLink";

type StatusTab = "pending" | "approved" | "rejected";

const DonationLinkReviewTable = () => {
  const client = useApolloClient();
  const [selectedStatus, setSelectedStatus] = useState<StatusTab>("pending");
  const [searchText, setSearchText] = useState("");
  const [gridPage, setGridPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDonation, setSelectedDonation] =
    useState<DonationLinkReviewItem | null>(null);
  const [linkWarning, setLinkWarning] = useState<{
    link: string;
    status: string;
  } | null>(null);
  const [lists, setLists] = useState<Record<StatusTab, DonationLinkReviewItem[]>>(
    {
      pending: [],
      approved: [],
      rejected: [],
    }
  );
  const [loading, setLoading] = useState(true);
  const [queryError, setQueryError] = useState<Error | null>(null);

  const [verifyDonation, { loading: verifying }] = useMutation(
    VERIFY_CUSTOM_DONATION
  );

  const loadStatus = useCallback(
    async (status: StatusTab) =>
      fetchAllPages(async (page, limit) => {
        const { data } = await client.query({
          query: GET_CUSTOM_DONATIONS_FOR_REVIEW,
          variables: { status, page, limit },
          fetchPolicy: "network-only",
        });
        return (data?.pendingCustomDonations ??
          []) as DonationLinkReviewItem[];
      }),
    [client]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setQueryError(null);
    try {
      const [pending, approved, rejected] = await Promise.all([
        loadStatus("pending"),
        loadStatus("approved"),
        loadStatus("rejected"),
      ]);
      setLists({ pending, approved, rejected });
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Failed to load donation links. Please try again.");
      setQueryError(error);
      toast.error(
        error.message || "Failed to load donation links. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [loadStatus]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const pending = lists.pending;
  const approved = lists.approved;
  const rejected = lists.rejected;

  const selectedData =
    selectedStatus === "pending"
      ? pending
      : selectedStatus === "approved"
      ? approved
      : rejected;

  const filteredData = useMemo(
    () => selectedData.filter((row) => matchesDonationSearch(row, searchText)),
    [selectedData, searchText]
  );

  useEffect(() => {
    setGridPage(0);
  }, [selectedStatus, searchText]);

  const handleTabChange = (status: StatusTab) => {
    setSelectedStatus(status);
    setSearchText("");
  };

  const handlePaymentLinkClick = (link: string, status: string) => {
    if (!isSafeHttpUrl(link)) {
      toast.error(
        "This payment link is not a valid http(s) URL and cannot be opened."
      );
      return;
    }
    if (isApprovedDonationStatus(status)) {
      openSafePaymentLink(link);
      return;
    }
    setLinkWarning({ link, status });
  };

  const handleActionClick = (row: DonationLinkReviewItem) => {
    setSelectedDonation(row);
    setModalOpen(true);
  };

  const handleConfirmAction = (
    action: "approved" | "rejected",
    reason: string
  ) => {
    if (!selectedDonation?._id) return;

    const toastId = toast.loading(
      action === "approved"
        ? "Approving donation link..."
        : "Rejecting donation link..."
    );

    verifyDonation({
      variables: {
        id: selectedDonation._id,
        approved: action === "approved",
        rejectionReason:
          action === "rejected" ? reason || undefined : undefined,
      },
    })
      .then(() => {
        toast.dismiss(toastId);
        toast.success(
          action === "approved"
            ? "Donation link approved"
            : "Donation link rejected"
        );
        setModalOpen(false);
        setSelectedDonation(null);
        setSelectedStatus(action === "approved" ? "approved" : "rejected");
        loadAll();
      })
      .catch((error) => {
        toast.dismiss(toastId);
        toast.error(error?.message || "Failed to update donation link");
        console.error("Donation link review error:", error);
      });
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={4} onClick={() => handleTabChange("pending")}>
          <Card
            sx={{
              borderRadius: "22px",
              cursor: "pointer",
              border:
                selectedStatus === "pending" ? "2px solid #1B8368" : "none",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                color="#1B8368"
                sx={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Box
                  component="img"
                  src={pendingIcon}
                  alt="pending"
                  sx={{ width: 28 }}
                />
                Pending
              </Typography>
              <Typography
                variant="h5"
                color="#1B8368"
                sx={{ fontWeight: "bold" }}
              >
                {formatTabCount(pending.length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4} onClick={() => handleTabChange("approved")}>
          <Card
            sx={{
              borderRadius: "22px",
              cursor: "pointer",
              border:
                selectedStatus === "approved" ? "2px solid #1B8368" : "none",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                color="#00D23B"
                sx={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Box
                  component="img"
                  src={approvedIcon}
                  alt="approved"
                  sx={{ width: 28 }}
                />
                Approved
              </Typography>
              <Typography
                variant="h5"
                color="#00D23B"
                sx={{ fontWeight: "bold" }}
              >
                {formatTabCount(approved.length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4} onClick={() => handleTabChange("rejected")}>
          <Card
            sx={{
              borderRadius: "22px",
              cursor: "pointer",
              border:
                selectedStatus === "rejected" ? "2px solid #1B8368" : "none",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                color="#F93737"
                sx={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Box
                  component="img"
                  src={deniedIcon}
                  alt="rejected"
                  sx={{ width: 28 }}
                />
                Rejected
              </Typography>
              <Typography
                variant="h5"
                color="#F93737"
                sx={{ fontWeight: "bold" }}
              >
                {formatTabCount(rejected.length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        {queryError && (
          <Box
            sx={{
              mx: 2.5,
              mt: 2,
              p: 2,
              borderRadius: "12px",
              bgcolor: "#FFF5F5",
              border: "1px solid #FECACA",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography color="#B91C1C" sx={{ fontSize: 14 }}>
              {queryError.message ||
                "Failed to load donation links. Please try again."}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={loadAll}
              sx={{
                textTransform: "none",
                borderColor: "#F93737",
                color: "#F93737",
                whiteSpace: "nowrap",
              }}
            >
              Retry
            </Button>
          </Box>
        )}

        <TextField
          fullWidth
          placeholder="Search by title, entity, or link"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ fontSize: "25px" }} />
              </IconButton>
            ),
          }}
          sx={{
            margin: "20px",
            width: "95%",
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
          }}
          variant="outlined"
        />

        <div style={{ height: 700, width: "100%" }}>
          <DataGrid
            loading={loading}
            getRowId={(row) => row._id}
            rows={filteredData}
            columns={[
              { field: "title", headerName: "Title", flex: 1.2 },
              {
                field: "entityName",
                headerName: "Entity Name",
                flex: 1,
                renderCell: (params) =>
                  params.row.entityName ? (
                    <DonationEntityIdLink
                      entityId={params.row.entityId}
                      entityType={params.row.entityType}
                      label={params.row.entityName}
                    />
                  ) : (
                    "—"
                  ),
              },
              {
                field: "entityType",
                headerName: "Entity Type",
                flex: 0.7,
                valueGetter: (params) => params.row.entityType || "—",
              },
              {
                field: "entityId",
                headerName: "Entity ID",
                flex: 1,
                renderCell: (params) => (
                  <DonationEntityIdLink
                    entityId={params.row.entityId}
                    entityType={params.row.entityType}
                  />
                ),
              },
              {
                field: "customPaymentLink",
                headerName: "Payment Link",
                flex: 1.4,
                renderCell: (params) => {
                  const link = params.row.customPaymentLink as string | null;
                  if (!link) return "—";
                  if (!isSafeHttpUrl(link)) {
                    return (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        title="Not a valid http(s) URL"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {link}
                      </Typography>
                    );
                  }
                  return (
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handlePaymentLinkClick(link, params.row.status);
                      }}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                        border: "none",
                        background: "none",
                        padding: 0,
                        font: "inherit",
                        color: "primary.main",
                        textDecoration: "underline",
                      }}
                    >
                      {link}
                    </Link>
                  );
                },
              },
              {
                field: "createdAt",
                headerName: "Created At",
                flex: 1,
                valueGetter: (params) =>
                  moment
                    .utc(params.row.createdAt)
                    .local()
                    .format("DD-MMM-YYYY HH:mm:ss"),
              },
              {
                field: "action",
                headerName: "Action",
                flex: 0.9,
                sortable: false,
                renderCell: (params) => {
                  const status = params.row.status;
                  let buttonText = "Review";
                  let buttonColor = "#1B8368";
                  let buttonIcon = exclamationIcon;
                  let iconSize = "16px";

                  if (status === "published" || status === "active") {
                    buttonText = "Approved";
                    buttonIcon = approvedBtnTick;
                    buttonColor = "#00D23B";
                  } else if (status === "rejected") {
                    buttonText = "Rejected";
                    buttonIcon = rejectedBtnCross;
                    buttonColor = "#F93737";
                    iconSize = "11px";
                  }

                  return (
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: buttonColor,
                        lineHeight: "1.60",
                        textTransform: "none",
                        borderRadius: "10px",
                        "&:hover": { bgcolor: buttonColor },
                      }}
                      onClick={() => handleActionClick(params.row)}
                    >
                      <Box
                        component="img"
                        src={buttonIcon}
                        width={iconSize}
                        marginRight="5px"
                      />
                      {buttonText}
                    </Button>
                  );
                },
              },
            ]}
            page={gridPage}
            pageSize={REVIEW_PAGE_SIZE}
            pagination
            onPageChange={setGridPage}
            rowsPerPageOptions={[REVIEW_PAGE_SIZE]}
            disableSelectionOnClick
          />
        </div>
      </Card>

      <DonationLinkReviewModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedDonation(null);
        }}
        donation={selectedDonation}
        onConfirmAction={handleConfirmAction}
        isLoading={verifying}
      />

      <PaymentLinkWarningDialog
        open={!!linkWarning}
        link={linkWarning?.link}
        status={linkWarning?.status}
        onClose={() => setLinkWarning(null)}
      />
    </Box>
  );
};

export default DonationLinkReviewTable;
